import { useLocation } from "@reach/router";
import { useWindowWidth } from "@react-hook/window-size";
import { graphql, useStaticQuery } from "gatsby";
import { useEffect, useState } from "react";

import { useThemeUI } from "@theme/components/ThemeUi";

import { currentMsMarket } from "@ms/gatsby-site/src/config/navigation";

export const HIDE_SUPER_BANNER_QUERY = graphql`
  query HideSuperBanner {
    allStrapiSite(filter: { Name: { ne: "__DO_NOT_REMOVE" } }) {
      nodes {
        Instance
        pages {
          slug
          showSuperBanner
        }
      }
    }
  }
`;
export interface NavHeightInterface {
  /** height of nav + super banner + category bar (on desktop) */
  currentNavHeight: number;
  // rarely we need to get the super banner height from this hook
  // so that we can get the current height and animate that content
  currentSuperBannerHeight: number;
  /** can be used as responsive array for sx props */
  navHeights: number[];
  /** can be used as responsive array for sx props */
  superBannerHeights: number[];
  /** is restricted page hides super banner and footer, currently only /cart and /offer pages */
  isRestrictedPage: boolean;
  /** hide super offer banner - hidden on restricted pages, account and strapi pages with no super banner */
  hideSpecialOfferBanner: boolean;
}

/**
 * Use Nav Height
 *
 * this hook makes it easy to calculate the various heights of the nav
 * It is useful for absolutely positioning various items when the nav is in its various states
 *
 * @returns
 */
export const useNavHeight = (): NavHeightInterface => {
  const { allStrapiSite } = useStaticQuery(HIDE_SUPER_BANNER_QUERY);
  const strapiHiddenBannerPages: string[] = allStrapiSite.nodes
    .find(
      (site: { Instance: string }) => site.Instance.toUpperCase() === currentMsMarket.toUpperCase(),
    )
    ?.pages.filter((page: { showSuperBanner: boolean }) => !page.showSuperBanner)
    .map((page: { slug: string }) => `/pages/${page.slug}`);

  const { pathname } = useLocation();

  // Nav bar with only home link
  const restrictedPages = ["/cart", "/offer"];
  const isRestrictedPage = restrictedPages.some((page) => pathname.includes(page));

  const noSuperBannerPages = [
    ...restrictedPages,
    ...(strapiHiddenBannerPages ? strapiHiddenBannerPages : []),
    "/account/",
  ];
  const hideSpecialOfferBanner = noSuperBannerPages.some(
    (page) => pathname.includes(page) && !pathname.includes("/pages/help/account-help"),
  );
  const { theme } = useThemeUI();
  const breakpoints = theme.breakpoints as string[];

  // these are all zero values for when nav features should be hidden
  const navHiddenHeights = theme.sizes.navHiddenHeight;

  const superBannerHeights = hideSpecialOfferBanner
    ? navHiddenHeights
    : theme.sizes.superBannerHeight;

  const navBarHeights = theme.sizes.navBarHeight;

  const categoryBarHeights = theme.sizes.categoryBarHeight;

  const navHeights = breakpoints.map((_, index: number) => {
    const navBarHeight = navBarHeights[index];
    const categoryBarHeight = categoryBarHeights[index];
    const superBannerHeight = superBannerHeights[index];

    return navBarHeight + categoryBarHeight + superBannerHeight;
  });

  // default current breakpoint at mobile version of navigation (produces less initial layout shift)
  const [currentBreakpoint, setCurrentBreakpoint] = useState(0);

  const initialWidth = parseInt(breakpoints[0]);

  // Set initial window width to last entry of breakpoints which contains the tallest navigation,
  // in case we get 0 returned when nothing has loaded
  const windowWidth = useWindowWidth({ initialWidth: initialWidth });

  // Find closest matching breakpoint and set value
  useEffect(() => {
    const found = breakpoints.find((point) => parseInt(point) > windowWidth);
    const index = found ? breakpoints.indexOf(found) : breakpoints.length - 1;
    if (currentBreakpoint === index) return;
    setCurrentBreakpoint(index);
  }, [breakpoints, windowWidth, currentBreakpoint]);

  return {
    currentNavHeight: navHeights[currentBreakpoint],
    currentSuperBannerHeight: superBannerHeights[currentBreakpoint],
    navHeights: navHeights,
    superBannerHeights: superBannerHeights,
    isRestrictedPage: isRestrictedPage,
    hideSpecialOfferBanner: hideSpecialOfferBanner,
  };
};
